import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Card,
  Button,
  Offcanvas,
  Form,
  InputGroup,
  Row,
  Col,
  Alert,
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../../config";
import Layout from "../../components/layouts/Main";
import { PulseLoader } from "react-spinners";
import $ from "jquery";
import "datatables.net-bs5";
import "datatables.net-responsive-bs5";
import AddProductModal from "../../components/products/AddProductModal";
import ProductModal from "../../components/products/ProductModal";
import { FaFilter, FaSearch } from "react-icons/fa";
import { toast } from "react-toastify";
import PermissionGate from "../../components/common/PermissionGate";
import { useHasPermission, PERMISSIONS } from "../../utils/permissions";

function ProductList() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState({
    category: "",
    isActive: "",
    group: "",
  });
  const [groups, setGroups] = useState([]);
  const tableRef = useRef(null);
  const dataTableRef = useRef(null);

  // Permission checks
  const canViewProducts = useHasPermission(PERMISSIONS.VIEW_PRODUCTS);

  const fetchProducts = useCallback(async () => {
    if (!canViewProducts) {
      toast.error("You don't have permission to view products");
      return;
    }

    setIsLoading(true);
    try {
      const res = await axios.get(
        `${config.API_BASE_URL}/products?category=all&minimal=true&populateGroup=true&populateUnit=true`
      );
      setProducts(res.data);
    } catch (err) {
      console.error("Failed to fetch products:", err);
      toast.error("Failed to load products");
    } finally {
      setIsLoading(false);
    }
  }, [canViewProducts]);

  const fetchGroups = useCallback(async () => {
    try {
      const res = await axios.get(`${config.API_BASE_URL}/groups`);
      setGroups(res.data);
    } catch (err) {
      console.error("Failed to fetch groups:", err);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
    fetchGroups();
  }, [fetchProducts, fetchGroups]);

  useEffect(() => {
    if (products.length > 0 && !isLoading && tableRef.current) {
      if (dataTableRef.current) {
        dataTableRef.current.destroy();
      }

      const dt = $(tableRef.current).DataTable({
        data: products,
        responsive: true,
        layout: {
          bottomEnd: {
            paging: {
              firstLast: false,
            },
          },
        },
        columnDefs: [
          {
            targets: 0, // SN column (0-based index)
            className: "text-start",
          },
        ],
        autoWidth: false,
        pagination: false,

        language: {
          processing: `<div class="text-center">
                      <div class="spinner-border spinner-border-sm text-primary" role="status">
                          <span class="visually-hidden">Loading...</span>
                      </div>
                  </div>`,
        },
        columns: [
          {
            data: "productName",
            title: "Product Name",
            render: (data, type, row) => `
                          <div>
                              <div class="fw-bold">${data}</div>
                              <div class="small text-muted">SKU: ${row.sku}</div>
                          </div>
                      `,
          },
          {
            data: "category",
            title: "Category",
            render: (data) => `
                          <span class="badge bg-${
                            data === "Row Material"
                              ? "primary"
                              : data === "Finished Good"
                              ? "success"
                              : "secondary"
                          }">${data}</span>
                      `,
          },
          {
            data: "groupId",
            title: "Group",
            render: (data) => data?.groupName || "-",
          },
          {
            data: "bufferStock",
            title: "Buffer Stock",
            render: (data, type, row) => `${data} ${row.unitId?.unitCode || ""}`,
          },
          {
            data: "isActive",
            title: "Status",
            render: function (data) {
            return data ?
              '<span class="badge bg-success">Active</span>' :
              '<span class="badge bg-danger">Inactive</span>';
          }
          },
          {
            data: null,
            title: "Actions",
            orderable: false,
            render: (data, type, row) => `
                          <div class="d-flex gap-2">
                              <button class="btn btn-sm btn-info view-btn" data-product-id="${row._id}">
                                  <i class="fas fa-eye"></i>
                              </button>
                          </div>
                      `,
          },
        ],
        dom: "Brtip", // Remove default search
        buttons: ["copy", "csv", "excel", "pdf", "print"],
        order: [[0, "asc"]],
        pageLength: 25,
      });

      dataTableRef.current = dt;

      // Custom search input
      $("#productSearch").off("keyup").on("keyup", function () {
        dt.search(this.value).draw();
      });

      // Handle button clicks
      $(tableRef.current).off("click").on("click", ".edit-btn", function () {
        const data = dt.row($(this).parents("tr")).data();
        if (data) {
          setSelectedProduct(data);
          setShowEditModal(true);
        }
      });

      $(tableRef.current).on("click", ".view-btn", function () {
        const productId = $(this).data('product-id');
        if (productId) {
          navigate(`/products/${productId}`);
        } else {
          console.error('Product ID not found for view action');
          toast.error('Unable to view product details');
        }
      });
    }

    // Cleanup on unmount
    return () => {
      if (dataTableRef.current) {
        dataTableRef.current.destroy();
        dataTableRef.current = null;
      }
      // Detach all event handlers on the table to prevent memory leaks
      if (tableRef.current) {
        $(tableRef.current).off();
      }
      $("#productSearch").off("keyup");
    };
  }, [products, isLoading, navigate]);

  const handleProductAdded = async (newProduct) => {
    try {
        setIsLoading(true);
        fetchProducts();
        toast.success("Product added successfully....");
    } catch (error) {
        console.error("Error refreshing products:", error);
        toast.error("Error refreshing product list");
    } finally {
        setIsLoading(false);
    }
};



  const applyFilters = () => {
    const dataTable = dataTableRef.current;
    if (!dataTable) return;

    // Clear any existing custom filters
    $.fn.dataTable.ext.search = [];

    // Add our custom filter function
    $.fn.dataTable.ext.search.push(function (settings, data, dataIndex) {
      const rowData = dataTable.row(dataIndex).data();

      // Category filter
      if (filters.category && rowData.category !== filters.category)
        return false;

      // Status filter
      if (
        filters.isActive !== "" &&
        rowData.isActive !== (filters.isActive === "true")
      )
        return false;

      // Group filter
      if (filters.group && rowData.groupId?._id !== filters.group) return false;

      return true;
    });

    dataTable.draw();
  };

  // If user can't view products, show permission denied
  if (!canViewProducts) {
    return (
      <Layout title="Products">
        <Alert variant="danger" className="d-flex align-items-center">
          <i className="fas fa-exclamation-triangle me-2"></i>
          <div>
            <strong>Access Denied</strong>
            <p className="mb-0">You don't have permission to view products. Contact your administrator for access.</p>
          </div>
        </Alert>
      </Layout>
    );
  }

  return (
    <Layout title="Products">
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/">Dashboard</Link>
          </li>
          <li className="breadcrumb-item active">Products</li>
        </ol>
      </nav>

      <Row className="mb-3">
        <Col>
          <h2>
            Products <span className="text-muted">({products.length})</span>
          </h2>
        </Col>
        <Col className="text-end">
          <div className="d-flex gap-2 justify-content-end">
            <Button
              variant="outline-primary"
              onClick={() => setShowFilters(true)}
            >
              <FaFilter /> Filters
            </Button>
            <PermissionGate
              require={PERMISSIONS.ADD_PRODUCTS}
              fallback={
                <Button variant="outline-primary" disabled title="You don't have permission to add products">
                  + Add Product
                </Button>
              }
            >
              <Button variant="primary" onClick={() => setShowAddModal(true)}>
                + Add Product
              </Button>
            </PermissionGate>
          </div>
        </Col>
      </Row>

      <Card className="mb-4">
        <Card.Body>
          {isLoading ? (
            <div className="text-center py-5">
              <PulseLoader color="#014b7cbb" size={9} />
            </div>
          ) : (
            <>
              <InputGroup className="mb-3">
                <InputGroup.Text>
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  id="productSearch"
                  placeholder="Search products..."
                  className="border-start-0"
                />
              </InputGroup>

              <div className="table-responsive">
                <table
                  ref={tableRef}
                  className="table table-bordered table-hover display responsive nowrap"
                ></table>
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      {/* Filter Offcanvas */}
      <Offcanvas
        show={showFilters}
        onHide={() => setShowFilters(false)}
        placement="end"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Filter Products</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Category</Form.Label>
              <Form.Select
                value={filters.category}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    category: e.target.value,
                  }))
                }
              >
                <option value="">All Categories</option>
                <option value="Row Material">Raw Material</option>
                <option value="Finished Good">Finished Good</option>
                <option value="Scrap">Scrap</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={filters.isActive}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    isActive: e.target.value,
                  }))
                }
              >
                <option value="">All Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Group</Form.Label>
              <Form.Select
                value={filters.group}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    group: e.target.value,
                  }))
                }
              >
                <option value="">All Groups</option>
                {groups.map((group) => (
                  <option key={group._id} value={group._id}>
                    {group.groupName}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <div className="d-grid gap-2">
              <Button
                variant="primary"
                onClick={() => {
                  applyFilters();
                  setShowFilters(false);
                }}
              >
                Apply Filters
              </Button>
            <Button
                variant="outline-secondary"
                onClick={() => {
                  setFilters({
                    category: "",
                    isActive: "",
                    group: "",
                  });
                  // Clear custom filters
                  $.fn.dataTable.ext.search = [];
                  if (dataTableRef.current) {
                    dataTableRef.current.search("").draw();
                  }
                }}
              >
                Clear Filters
              </Button>
            </div>
          </Form>
        </Offcanvas.Body>
      </Offcanvas>

      <AddProductModal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        onProductAdded={handleProductAdded}
      />

      {selectedProduct && (
        <ProductModal
          show={showEditModal}
          onHide={() => {
            setShowEditModal(false);
            setSelectedProduct(null);
          }}
          product={selectedProduct}
          onUpdated={() => {
            fetchProducts();
            toast.success("Product updated successfully");
          }}
        />
      )}
    </Layout>
  );
}

export default ProductList;
